/**
 * ShareAnalysisModal Component
 * Modal dialog for sharing analysis with external recipients via double opt-in
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
} from '@/components/common/GlassModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useShareAnalysis } from '@/hooks/useShareAnalysis';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { createShareAnalysisSchema, type ShareAnalysisFormData } from './schema';

export interface ShareAnalysisModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Handler for open state changes */
  onOpenChange: (open: boolean) => void;
  /** The correlation ID of the analysis to share */
  correlationId: string;
}

type ModalStep = 'form' | 'success' | 'error';

/**
 * Share analysis modal with form, success, and error states
 */
export function ShareAnalysisModal({
  open,
  onOpenChange,
  correlationId,
}: ShareAnalysisModalProps) {
  const { t } = useTranslation();
  const { trackShare } = useFeatureTracking();
  const [step, setStep] = useState<ModalStep>('form');
  const [recipientEmail, setRecipientEmail] = useState('');

  const shareMutation = useShareAnalysis();

  // Create validation schema with translations
  const schema = createShareAnalysisSchema(t);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ShareAnalysisFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      recipientEmail: '',
      recipientFirstName: '',
      recipientLastName: '',
      personalMessage: '',
      acceptTerms: false,
    },
  });

  // Reset form and step when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setStep('form');
      setRecipientEmail('');
      shareMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const handleFormSubmit = async (data: ShareAnalysisFormData) => {
    setRecipientEmail(data.recipientEmail);

    try {
      await shareMutation.mutateAsync({
        correlationId,
        recipientEmail: data.recipientEmail,
        recipientFirstName: data.recipientFirstName || undefined,
        recipientLastName: data.recipientLastName || undefined,
        personalMessage: data.personalMessage || undefined,
      });
      trackShare('created');
      setStep('success');
    } catch {
      setStep('error');
    }
  };

  const handleRetry = () => {
    setStep('form');
    shareMutation.reset();
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const inputClassName = cn(
    'bg-white/50 border-[var(--glass-border)]',
    'focus:border-[var(--gradient-start)] focus:ring-[var(--gradient-start)]'
  );

  const labelClassName = 'text-[var(--color-charcoal)] font-medium';

  return (
    <GlassModal open={open} onOpenChange={onOpenChange}>
      <GlassModalContent
        className="max-w-3xl w-full max-h-[90dvh] overflow-y-auto"
        showCloseButton={false}
        aria-describedby="share-dialog-description"
      >
        <div className="relative">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label={t('common.close')}
            className="absolute right-0 top-0 text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)]"
          >
            <X className="h-5 w-5" />
          </Button>

          {step === 'form' && (
            <>
              <GlassModalHeader className="pr-8">
                <GlassModalTitle>{t('results.share.modalTitle')}</GlassModalTitle>
                <GlassModalDescription id="share-dialog-description">
                  {t('results.share.modalDescription')}
                </GlassModalDescription>
              </GlassModalHeader>

              <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-5">
                {/* Recipient Email */}
                <div className="space-y-2">
                  <Label htmlFor="recipientEmail" className={labelClassName}>
                    {t('results.share.recipientEmail')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="recipientEmail"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={t('results.share.recipientEmailPlaceholder')}
                    className={cn(inputClassName, errors.recipientEmail && 'border-red-500')}
                    aria-invalid={!!errors.recipientEmail}
                    aria-describedby={errors.recipientEmail ? 'recipientEmail-error' : undefined}
                    {...register('recipientEmail')}
                  />
                  {errors.recipientEmail && (
                    <p id="recipientEmail-error" className="text-sm text-red-500">
                      {errors.recipientEmail.message}
                    </p>
                  )}
                </div>

                {/* Recipient Name fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="recipientFirstName" className={labelClassName}>
                      {t('results.share.recipientFirstName')}
                    </Label>
                    <Input
                      id="recipientFirstName"
                      type="text"
                      autoComplete="given-name"
                      placeholder={t('results.share.recipientFirstNamePlaceholder')}
                      className={inputClassName}
                      {...register('recipientFirstName')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recipientLastName" className={labelClassName}>
                      {t('results.share.recipientLastName')}
                    </Label>
                    <Input
                      id="recipientLastName"
                      type="text"
                      autoComplete="family-name"
                      placeholder={t('results.share.recipientLastNamePlaceholder')}
                      className={inputClassName}
                      {...register('recipientLastName')}
                    />
                  </div>
                </div>

                {/* Personal Message (optional) */}
                <div className="space-y-2">
                  <Label htmlFor="personalMessage" className={labelClassName}>
                    {t('results.share.personalMessage')}
                  </Label>
                  <textarea
                    id="personalMessage"
                    rows={3}
                    placeholder={t('results.share.personalMessagePlaceholder')}
                    className={cn(
                      'w-full rounded-md border px-3 py-2 text-sm resize-none',
                      inputClassName,
                      errors.personalMessage && 'border-red-500'
                    )}
                    aria-invalid={!!errors.personalMessage}
                    aria-describedby={errors.personalMessage ? 'personalMessage-error' : undefined}
                    {...register('personalMessage')}
                  />
                  {errors.personalMessage && (
                    <p id="personalMessage-error" className="text-sm text-red-500">
                      {errors.personalMessage.message}
                    </p>
                  )}
                </div>

                {/* Accept Terms Checkbox */}
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className={cn(
                        'mt-1 h-5 w-5 min-w-5 rounded border-[var(--glass-border)]',
                        'text-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]',
                        errors.acceptTerms && 'border-red-500'
                      )}
                      {...register('acceptTerms')}
                    />
                    <span className="text-sm text-[var(--color-charcoal)]/80">
                      {t('results.share.acceptTerms')} <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {errors.acceptTerms && (
                    <p className="text-sm text-red-500">
                      {errors.acceptTerms.message}
                    </p>
                  )}
                </div>

                {/* Submit button */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-[var(--glass-border)]">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="w-full sm:w-auto"
                  >
                    {t('results.share.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    data-track="share_submit"
                    disabled={shareMutation.isPending}
                    className="btn-primary w-full sm:w-auto"
                  >
                    {shareMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('results.share.sending')}
                      </>
                    ) : (
                      t('results.share.send')
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-[var(--color-primary-start)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--color-charcoal)] mb-2">
                {t('results.share.successTitle')}
              </h3>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-6">
                {t('results.share.successDescription', { email: recipientEmail })}
              </p>
              <Button onClick={handleClose} className="btn-primary">
                {t('results.share.done')}
              </Button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--color-charcoal)] mb-2">
                {t('results.share.errorTitle')}
              </h3>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-6">
                {t('results.share.errorDescription')}
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={handleClose}>
                  {t('results.share.cancel')}
                </Button>
                <Button onClick={handleRetry} className="btn-primary">
                  {t('results.share.retry')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </GlassModalContent>
    </GlassModal>
  );
}

export default ShareAnalysisModal;
